import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { settings } from '@/config';
import type { Renderer } from '@/renderer/Renderer';
import type { Resizable, SizesState } from '@/types';

/**
 * Prepared post-processing pipeline (disabled by default). Holds an
 * EffectComposer with RenderPass → OutputPass (OutputPass restores tone mapping
 * + color space once the composer owns final output). Future effects (bloom,
 * DOF, vignette) are inserted before the OutputPass.
 *
 * While `enabled` is false the Experience renders directly through the renderer,
 * so the composer adds zero cost in Phase 01.
 */
export class PostProcessing implements Resizable {
  readonly composer: EffectComposer;
  enabled = settings.postprocessing.enabled;

  private readonly renderPass: RenderPass;
  readonly bloomPass: UnrealBloomPass | null = null;

  constructor(private readonly renderer: Renderer, scene: THREE.Scene, camera: THREE.Camera, sizes: SizesState) {
    // GOTCHA: `renderer.antialias` (the WebGL context flag, `settings.renderer.antialias`)
    // only smooths edges when rendering DIRECTLY to the canvas — the default framebuffer's
    // native MSAA. EffectComposer renders into an offscreen WebGLRenderTarget instead, which
    // has NO multisampling unless one is explicitly requested, so every edge in the app was
    // aliased despite `antialias: true` (postprocessing/bloom is on by default). Fixed by
    // passing an explicit MSAA render target here, capped to what the GPU actually supports.
    const pixelRatio = Math.min(sizes.pixelRatio, settings.renderer.maxPixelRatio) * renderer.getPixelRatioScale();
    const msaaSamples = Math.min(4, renderer.instance.capabilities.maxSamples);
    const renderTarget = new THREE.WebGLRenderTarget(
      sizes.width * pixelRatio,
      sizes.height * pixelRatio,
      { type: THREE.HalfFloatType, samples: msaaSamples },
    );
    this.composer = new EffectComposer(renderer.instance, renderTarget);
    this.composer.setPixelRatio(pixelRatio);
    this.composer.setSize(sizes.width, sizes.height);

    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // Bloom: makes the bright yellow emissive stones spill light into their
    // surroundings (the igloo-style glow), leaving the calmer scene untouched.
    const bloom = settings.postprocessing.bloom;
    if (bloom.enabled) {
      this.bloomPass = new UnrealBloomPass(
        new THREE.Vector2(sizes.width, sizes.height),
        bloom.strength,
        bloom.radius,
        bloom.threshold,
      );
      this.composer.addPass(this.bloomPass);
    }

    // OutputPass restores tone mapping + sRGB once the composer owns final output.
    this.composer.addPass(new OutputPass());
  }

  setCamera(camera: THREE.Camera): void {
    this.renderPass.camera = camera;
  }

  render(): void {
    this.composer.render();
  }

  resize(sizes: SizesState): void {
    // MUST match `Renderer.applySize()`'s effective pixel ratio (base ratio × the
    // adaptive-quality `pixelRatioScale` multiplier) — this composer owns its OWN
    // fixed-size WebGLRenderTarget (constructed once, above), separate from the
    // renderer's own drawing buffer. If the renderer's actual output size changes
    // (a window resize, OR `Renderer.setPixelRatioScale()` dropping resolution under
    // adaptive quality) without this composer target following it, the mismatched
    // sizes threw a real WebGL "Feedback loop formed between Framebuffer and active
    // Texture" error (2026-07-05) — `setPixelRatioScale` used to only resize the
    // renderer itself, never this composer, so a quality-tier drop mid-session (e.g.
    // triggered by 2+ simultaneous reflective water surfaces getting expensive) left
    // this target stale relative to the renderer's new output size.
    const pixelRatio = Math.min(sizes.pixelRatio, settings.renderer.maxPixelRatio) * this.renderer.getPixelRatioScale();
    this.composer.setPixelRatio(pixelRatio);
    this.composer.setSize(sizes.width, sizes.height);
    this.bloomPass?.setSize(sizes.width, sizes.height);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
